import { formatOeDisplay, formatJpWithAlias } from '@/utils/partDisplay';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Search, Plus, Minus, Trash2, User, Building2, Percent, Package, X, Check, ShoppingBag, AlertCircle, Save, Clock, Play, RefreshCw, UserPlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import BangladeshExportOrderModal from './BangladeshExportOrderModal';
import QuickRegisterCustomer from './QuickRegisterCustomer';
import TransporterComboBox from './TransporterComboBox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local storage key for draft
const ORDER_DRAFT_KEY = 'order_draft';

const CreateCustomerOrder = ({ isOpen, onClose, scopeToDistributorId = null }) => {
  const { token, user } = useAuth();
  const [step, setStep] = useState(1); // 1: Select Customer, 2: Build Cart, 3: Review
  
  // Customer Selection
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerDiscount, setCustomerDiscount] = useState(0);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState(0);
  const [specialPrices, setSpecialPrices] = useState({}); // {jp_part_no: special_price}
  const [inlineHandshakePrices, setInlineHandshakePrices] = useState({}); // {jp_part_no: price} - prices entered in popup
  
  // Product Search & Cart
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [cart, setCart] = useState([]);
  const [productImages, setProductImages] = useState({});
  
  // Dispatch Details
  const [dispatchMode, setDispatchMode] = useState('transport');
  const [transporterName, setTransporterName] = useState('');
  const [dispatchRemarks, setDispatchRemarks] = useState('');
  
  // Delhi customer detection
  const isDelhiCustomer = selectedCustomer && (
    String(selectedCustomer.pincode || '').startsWith('110') ||
    String(selectedCustomer.city || '').toLowerCase().includes('delhi') ||
    String(selectedCustomer.state || '').toLowerCase().includes('delhi')
  );
  const isMasterAdmin = user?.mobile === '9999313175';
  const isDispatchLocked = isDelhiCustomer && !isMasterAdmin;
  
  // Add to Cart Dialog
  const [addToCartProduct, setAddToCartProduct] = useState(null);
  const [addToCartQuantity, setAddToCartQuantity] = useState(0);
  const [addToCartHandshakePrice, setAddToCartHandshakePrice] = useState(''); // Empty string = use default
  const qtyInputRef = useRef(null);
  
  // Duplicate Item Confirmation Dialog
  const [duplicateItemConfirm, setDuplicateItemConfirm] = useState(null); // {product, existingQty, newQty, handshakePrice}
  
  // Order Submission
  const [submitting, setSubmitting] = useState(false);
  
  // Close confirmation dialog
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  
  // Draft recovery dialog (localStorage)
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  
  // Server-side drafts (Pending for Completion)
  const [serverDrafts, setServerDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  
  // Bangladesh Export Modal state
  const [showBangladeshModal, setShowBangladeshModal] = useState(false);
  const [bangladeshCustomer, setBangladeshCustomer] = useState(null);
  
  // Quick Register Customer state
  const [showQuickRegister, setShowQuickRegister] = useState(false);
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = cart.length > 0 || selectedCustomer !== null;
  
  // Auto-set dispatch to Local for Delhi customers
  useEffect(() => {
    if (isDelhiCustomer) {
      setDispatchMode('local');
      setTransporterName('');
    }
  }, [isDelhiCustomer]);

  // Check if customer is from Bangladesh (country_code: 880)
  const isBangladeshCustomer = (customer) => customer?.country_code === '880';
  
  // Auto-save draft to localStorage whenever cart or customer changes
  // Uses compressed format: stores only {part_no: quantity} to support 500+ items
  useEffect(() => {
    if (isOpen && (cart.length > 0 || selectedCustomer)) {
      try {
        // Compress cart to minimal format: {part_no: {qty, price, name}}
        const compressedCart = {};
        cart.forEach(item => {
          compressedCart[item.jp_part_no] = {
            q: item.quantity,
            p: item.price || item.mrp,
            n: item.part_name?.substring(0, 50), // Truncate name to save space
            dp: item.discounted_price,
            img: item.image ? 'y' : 'n' // Just flag if has image
          };
        });
        
        const draft = {
          cc: compressedCart, // compressed cart
          cs: selectedCustomer ? {
            id: selectedCustomer.customer_id,
            n: selectedCustomer.name,
            cn: selectedCustomer.company_name,
            m: selectedCustomer.mobile,
            d: selectedCustomer.discount_percentage,
            st: selectedCustomer.state,
            ct: selectedCustomer.city,
            pc: selectedCustomer.pincode
          } : null,
          cd: customerDiscount,
          sp: specialPrices,
          ihp: inlineHandshakePrices,
          dm: dispatchMode,
          tn: transporterName,
          dr: dispatchRemarks,
          st: step,
          ts: Date.now()
        };
        localStorage.setItem(ORDER_DRAFT_KEY, JSON.stringify(draft));
      } catch (error) {
        console.warn('localStorage save failed:', error.message);
      }
    }
  }, [isOpen, cart, selectedCustomer, customerDiscount, specialPrices, inlineHandshakePrices, dispatchMode, transporterName, dispatchRemarks, step]);
  
  // Check for saved draft when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedDraftStr = localStorage.getItem(ORDER_DRAFT_KEY);
      if (savedDraftStr) {
        try {
          const draft = JSON.parse(savedDraftStr);
          // Check if it's compressed format (has 'cc' key) or old format (has 'cart' key)
          const hasItems = draft.cc ? Object.keys(draft.cc).length > 0 : draft.cart?.length > 0;
          const hasCustomer = draft.cs || draft.selectedCustomer;
          
          if (hasItems || hasCustomer) {
            setSavedDraft(draft);
            setShowDraftRecovery(true);
          }
        } catch (e) {
          console.error('Error parsing saved draft:', e);
          localStorage.removeItem(ORDER_DRAFT_KEY);
        }
      }
    }
  }, [isOpen]);
  
  // Restore draft function - handles both compressed and old format
  const restoreDraft = async () => {
    if (!savedDraft) return;
    
    // Check if it's compressed format
    if (savedDraft.cc) {
      // Compressed format - need to fetch full part details
      const partNumbers = Object.keys(savedDraft.cc);
      if (partNumbers.length > 0) {
        try {
          // Fetch part details from server
          const response = await axios.post(`${API}/products/bulk-details`, {
            part_numbers: partNumbers
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const partsMap = {};
          (response.data.products || []).forEach(p => {
            partsMap[p.jp_part_no] = p;
          });
          
          // Reconstruct full cart
          const restoredCart = partNumbers.map(partNo => {
            const compressed = savedDraft.cc[partNo];
            const fullPart = partsMap[partNo] || {};
            return {
              jp_part_no: partNo,
              part_name: fullPart.part_name || compressed.n || partNo,
              oe_part_no: fullPart.oe_part_no || '',
              mrp: fullPart.mrp || compressed.p || 0,
              price: compressed.p || fullPart.mrp || 0,
              discounted_price: compressed.dp || fullPart.discounted_price,
              quantity: compressed.q || 1,
              image: fullPart.image || null
            };
          });
          
          setCart(restoredCart);
        } catch (error) {
          console.error('Error fetching part details:', error);
          // Fallback: restore with minimal data
          const restoredCart = partNumbers.map(partNo => {
            const compressed = savedDraft.cc[partNo];
            return {
              jp_part_no: partNo,
              part_name: compressed.n || partNo,
              mrp: compressed.p || 0,
              price: compressed.p || 0,
              discounted_price: compressed.dp,
              quantity: compressed.q || 1
            };
          });
          setCart(restoredCart);
        }
      }
      
      // Restore customer from compressed format
      if (savedDraft.cs) {
        const restoredCustomer = {
          customer_id: savedDraft.cs.id,
          name: savedDraft.cs.n,
          company_name: savedDraft.cs.cn,
          mobile: savedDraft.cs.m,
          discount_percentage: savedDraft.cs.d,
          state: savedDraft.cs.st,
          city: savedDraft.cs.ct,
          pincode: savedDraft.cs.pc
        };
        setSelectedCustomer(restoredCustomer);
        setCustomerDiscount(savedDraft.cd || savedDraft.cs.d || 0);
        
        // Re-fetch customer data if address fields missing (for Delhi detection)
        if (!savedDraft.cs.st && !savedDraft.cs.ct && !savedDraft.cs.pc && savedDraft.cs.m) {
          try {
            const res = await axios.get(`${API}/admin/customers/search?q=${savedDraft.cs.m}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const freshCustomer = res.data?.customers?.find(c => c.mobile === savedDraft.cs.m);
            if (freshCustomer) {
              setSelectedCustomer(prev => ({ ...prev, state: freshCustomer.state, city: freshCustomer.city, pincode: freshCustomer.pincode }));
            }
          } catch (e) { /* silent fail */ }
        }
      }
      
      setSpecialPrices(savedDraft.sp || {});
      setInlineHandshakePrices(savedDraft.ihp || {});
      setDispatchMode(savedDraft.dm || 'transport');
      setTransporterName(savedDraft.tn || '');
      setDispatchRemarks(savedDraft.dr || '');
      setStep(savedDraft.st >= 2 ? savedDraft.st : (savedDraft.cs ? 2 : 1));
    } else {
      // Old format - direct restore
      setCart(savedDraft.cart || []);
      setSelectedCustomer(savedDraft.selectedCustomer || null);
      setCustomerDiscount(savedDraft.customerDiscount || 0);
      setSpecialPrices(savedDraft.specialPrices || {});
      setInlineHandshakePrices(savedDraft.inlineHandshakePrices || {});
      setDispatchMode(savedDraft.dispatchMode || 'transport');
      setTransporterName(savedDraft.transporterName || '');
      setDispatchRemarks(savedDraft.dispatchRemarks || '');
      setStep(savedDraft.step || (savedDraft.selectedCustomer ? 2 : 1));
      toast.success('Draft restored successfully!');
    }
    
    setShowDraftRecovery(false);
    setSavedDraft(null);
  };
  
  // Discard draft function
  const discardDraft = () => {
    localStorage.removeItem(ORDER_DRAFT_KEY);
    setShowDraftRecovery(false);
    setSavedDraft(null);
  };
  
  // ==================== SERVER-SIDE DRAFTS ====================
  
  // Fetch server-side drafts when modal opens
  const fetchServerDrafts = useCallback(async () => {
    if (!token) return;
    setLoadingDrafts(true);
    try {
      const response = await axios.get(`${API}/drafts/my?draft_type=order`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerDrafts(response.data.drafts || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    } finally {
      setLoadingDrafts(false);
    }
  }, [token]);
  
  useEffect(() => {
    if (isOpen) {
      fetchServerDrafts();
    }
  }, [isOpen, fetchServerDrafts]);
  
  // Save progress to server
  const saveProgressToServer = async () => {
    if (!selectedCustomer || cart.length === 0) {
      toast.error('Please select a customer and add items first');
      return;
    }
    
    setSavingDraft(true);
    try {
      await axios.post(`${API}/drafts/save`, {
        draft_type: 'order',
        customer_id: selectedCustomer.customer_id,
        customer_name: selectedCustomer.name,
        company_name: selectedCustomer.company_name,
        customer_phone: selectedCustomer.mobile,
        customer_email: selectedCustomer.email,
        customer_gst: selectedCustomer.gst_number,
        customer_address: selectedCustomer.address,
        customer_state: selectedCustomer.state,
        customer_category: selectedCustomer.customer_category,
        customer_discount: customerDiscount,
        items: cart,
        special_prices: specialPrices,
        inline_handshake_prices: inlineHandshakePrices,
        dispatch_mode: dispatchMode,
        transporter_name: transporterName,
        dispatch_remarks: dispatchRemarks,
        step: step,
        total_amount: calculateTotal(),
        // Store full customer object for resume
        full_customer: selectedCustomer
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Progress saved! You can resume this order later.');
      // Clear localStorage draft since we saved to server
      localStorage.removeItem(ORDER_DRAFT_KEY);
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save progress');
    } finally {
      setSavingDraft(false);
    }
  };
  
  // Resume a server-side draft
  const resumeDraft = async (draft) => {
    try {
      // Use customer data stored in draft
      const customer = draft.full_customer || {
        customer_id: draft.customer_id,
        name: draft.customer_name,
        company_name: draft.company_name,
        mobile: draft.customer_phone,
        email: draft.customer_email,
        gst_number: draft.customer_gst,
        address: draft.customer_address,
        state: draft.customer_state,
        customer_category: draft.customer_category,
        discount_percentage: draft.customer_discount
      };
      
      setSelectedCustomer(customer);
      setCustomerDiscount(draft.customer_discount || customer.discount_percentage || 0);
      setSpecialPrices(draft.special_prices || {});
      setInlineHandshakePrices(draft.inline_handshake_prices || {});
      setCart(draft.items || []);
      setDispatchMode(draft.dispatch_mode || 'transport');
      setTransporterName(draft.transporter_name || '');
      setDispatchRemarks(draft.dispatch_remarks || '');
      setStep(draft.step >= 2 ? draft.step : 2);
      
      toast.success('Draft loaded! Continue where you left off.');
    } catch (error) {
      console.error('Error resuming draft:', error);
      toast.error('Failed to load draft');
    }
  };
  
  // Delete a server-side draft
  const deleteServerDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Draft deleted');
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };
  
  // Delete draft after successful order submission
  const deleteDraftForCustomer = async (customerId) => {
    try {
      await axios.delete(`${API}/drafts/customer/${customerId}?draft_type=order`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error deleting customer draft:', error);
    }
  };
  
  // Load customer's preferred/last transporter when selected
  useEffect(() => {
    const transporter = selectedCustomer?.preferred_transporter || selectedCustomer?.last_transporter;
    if (transporter) {
      setTransporterName(transporter);
    } else {
      setTransporterName('');
    }
  }, [selectedCustomer]);
  
  // Auto-select quantity input when dialog opens
  useEffect(() => {
    if (addToCartProduct && qtyInputRef.current) {
      // Use multiple attempts to ensure the input gets focused and selected
      const focusAndSelect = () => {
        if (qtyInputRef.current) {
          qtyInputRef.current.focus();
          qtyInputRef.current.select();
        }
      };
      
      // Try immediately
      focusAndSelect();
      
      // Try after animation frame
      requestAnimationFrame(() => {
        focusAndSelect();
      });
      
      // Try after short delay (for dialog animation)
      const timer1 = setTimeout(focusAndSelect, 50);
      const timer2 = setTimeout(focusAndSelect, 150);
      const timer3 = setTimeout(focusAndSelect, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [addToCartProduct]);

  // Debounce search
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };
  
  // Request ID refs for stale result prevention
  const customerSearchIdRef = useRef(0);
  const productSearchIdRef = useRef(0);

  const debouncedCustomerSearch = useDebounce(customerSearch, 500);
  // 2026-05: drop product debounce from 500ms → 250ms. The backend
  // search is already ~200ms warm; a fat half-second pre-debounce
  // made the UI *feel* slow even though the network was fine.
  const debouncedProductSearch = useDebounce(productSearch, 250);

  // Search Customers
  useEffect(() => {
    if (debouncedCustomerSearch.trim().length >= 2) {
      searchCustomers(debouncedCustomerSearch);
    } else {
      setCustomers([]);
    }
  }, [debouncedCustomerSearch]);

  const searchCustomers = async (query) => {
    const thisRequestId = ++customerSearchIdRef.current;
    const thisQuery = query.trim();
    
    setLoadingCustomers(true);
    try {
      // Scope search to distributor's sub-customers when called from distributor panel
      const url = scopeToDistributorId
        ? `${API}/distributor-panel/customers?distributor_id=${scopeToDistributorId}&search=${encodeURIComponent(thisQuery)}`
        : `${API}/admin/customers/search?q=${encodeURIComponent(thisQuery)}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Only update if this is still the current request (prevents race conditions)
      if (thisRequestId === customerSearchIdRef.current) {
        setCustomers(response.data.customers || []);
      }
    } catch (error) {
      if (thisRequestId === customerSearchIdRef.current) {
        console.error('Error searching customers:', error);
        setCustomers([]);
      }
    } finally {
      if (thisRequestId === customerSearchIdRef.current) {
        setLoadingCustomers(false);
      }
    }
  };

  // Search Products with abort + query-match check.
  //
  // 2026-05 race-condition fix: relying only on `productSearchIdRef`
  // missed edge cases — sometimes a short query like "BU" finished
  // slower than "BUMP" and overwrote the displayed results. We now
  // belt-and-suspenders this with TWO guards:
  //   1. AbortController cancels the prior request on cleanup.
  //   2. At response time we compare the original query against the
  //      currently-debounced query. If they differ, drop the result
  //      regardless of request-id. This handles the "ref re-init on
  //      effect remount" edge case the prior code missed.
  useEffect(() => {
    const abortController = new AbortController();
    const thisQuery = debouncedProductSearch.trim();
    const doSearch = async () => {
      if (thisQuery.length >= 2 && selectedCustomer && token) {
        const thisRequestId = ++productSearchIdRef.current;
        setLoadingProducts(true);
        try {
          const response = await axios.get(
            `${API}/categories/All%20Products/parts?search=${encodeURIComponent(thisQuery)}&limit=20&include_inactive_brands=1&include_admin_only=true`,
            { headers: { Authorization: `Bearer ${token}` }, signal: abortController.signal }
          );
          // Guard 1: request-id is current (defends against ref-shared races).
          if (thisRequestId !== productSearchIdRef.current) return;
          // Guard 2: the query we kicked off still matches the latest
          // debounced query. If the user kept typing, drop this result
          // even if our request-id check passed.
          if (thisQuery !== debouncedProductSearch.trim()) return;
          const parts = response.data?.parts || response.data || [];
          setSearchResults(Array.isArray(parts) ? parts : []);
          if (Array.isArray(parts)) {
            parts.forEach(part => {
              if (part.image && !productImages[part.jp_part_no]) {
                loadProductImage(part.jp_part_no, part.image);
              }
            });
          }
        } catch (error) {
          if (error.name === 'AbortError' || error.name === 'CanceledError') return;
          if (thisRequestId === productSearchIdRef.current
              && thisQuery === debouncedProductSearch.trim()) {
            console.error('Error searching products:', error);
            setSearchResults([]);
          }
        } finally {
          if (thisRequestId === productSearchIdRef.current) setLoadingProducts(false);
        }
      } else {
        setSearchResults([]);
      }
    };
    doSearch();
    return () => abortController.abort();
  }, [debouncedProductSearch, selectedCustomer, token]);

  // Load product image as data URL
  const loadProductImage = async (partNo, imageName) => {
    try {
      const response = await axios.get(`${API}/image/${imageName}`);
      if (response.data.data_url) {
        setProductImages(prev => ({ ...prev, [partNo]: response.data.data_url }));
      }
    } catch (error) {
      console.error('Error loading image:', error);
    }
  };

  // Select Customer
  const handleSelectCustomer = async (customer) => {
    // Check if customer is from Bangladesh - redirect to Bangladesh Export Order flow
    if (isBangladeshCustomer(customer)) {
      setBangladeshCustomer(customer);
      setShowBangladeshModal(true);
      setCustomerSearch('');
      setCustomers([]);
      return; // Don't continue with domestic order flow
    }
    
    setSelectedCustomer(customer);
    setCustomerDiscount(customer.discount_percentage || 0);
    setNewDiscount(customer.discount_percentage || 0);
    setCustomerSearch('');
    setCustomers([]);
    
    // Fetch special prices for this customer
    try {
      const response = await axios.get(
        `${API}/price-requests/admin/special-prices`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { customer_id: customer.customer_id },
        }
      );
      const allSpecialPrices = response.data.special_prices || [];

      // Backend already scoped to this customer; still filter as a
      // safety net in case an older cached client hits a new API.
      const customerSpecialPrices = {};
      allSpecialPrices
        .filter(sp => sp.customer_id === customer.customer_id)
        .forEach(sp => {
          customerSpecialPrices[sp.jp_part_no] = sp.special_price;
        });
      
      setSpecialPrices(customerSpecialPrices);
      
      if (Object.keys(customerSpecialPrices).length > 0) {
        toast.success(`Found ${Object.keys(customerSpecialPrices).length} special prices for this customer`);
      }
    } catch (error) {
      console.error('Error fetching special prices:', error);
      setSpecialPrices({});
    }
    
    setStep(2);
  };

  // Update Customer Discount
  const handleUpdateDiscount = async () => {
    try {
      await axios.put(`${API}/admin/customers/${selectedCustomer.mobile}/discount`, 
        { discount_percentage: parseFloat(newDiscount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerDiscount(parseFloat(newDiscount));
      setSelectedCustomer(prev => ({ ...prev, discount_percentage: parseFloat(newDiscount) }));
      setEditingDiscount(false);
      toast.success('Discount updated');
    } catch (error) {
      toast.error('Failed to update discount');
    }
  };

  // Cart Functions
  const openAddToCartDialog = (product) => {
    setAddToCartProduct(product);
    // 2026-02-20 RC-151 — default qty to warehouse MOQ so admin gets
    // one full pack, not `0`/`1`.
    const moq = Math.max(1, parseInt(product?.warehouse_moq || 1, 10));
    setAddToCartQuantity(moq);
    // Pre-fill handshake price if customer already has a special price for this item
    const existingSpecialPrice = specialPrices[product.jp_part_no];
    setAddToCartHandshakePrice(existingSpecialPrice ? existingSpecialPrice.toString() : '');
  };

  const confirmAddToCart = () => {
    if (!addToCartProduct) return;
    if (addToCartQuantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    // Check if item already exists in cart
    const existing = cart.find(item => item.jp_part_no === addToCartProduct.jp_part_no);
    if (existing) {
      // Show confirmation dialog for duplicate item
      setDuplicateItemConfirm({
        product: addToCartProduct,
        existingQty: existing.quantity,
        newQty: addToCartQuantity,
        handshakePrice: addToCartHandshakePrice.trim()
      });
      return;
    }
    
    // No duplicate - add directly
    addItemToCart(addToCartProduct, addToCartQuantity, addToCartHandshakePrice.trim());
  };
  
  // Actual function to add/merge item to cart
  const addItemToCart = (product, quantity, handshakePrice, mergeWithExisting = false) => {
    // Store handshake price if entered
    if (handshakePrice && parseFloat(handshakePrice) > 0) {
      setInlineHandshakePrices(prev => ({
        ...prev,
        [product.jp_part_no]: parseFloat(handshakePrice)
      }));
    }

    // 2026-02-20 RC-151 — Snap the incoming qty to a warehouse_moq
    // multiple BEFORE inserting into the cart. Defaults to `moq`
    // when qty was 0/blank so an admin who leaves the qty field
    // untouched gets one full pack, not `1`.
    const moq = Math.max(1, parseInt(product.warehouse_moq || 1, 10));
    const snapQty = (q) => {
      const n = Math.max(1, parseInt(q || 0, 10) || moq);
      if (moq <= 1) return n;
      return Math.ceil(n / moq) * moq;
    };
    const snappedQty = snapQty(quantity);

    const existing = cart.find(item => item.jp_part_no === product.jp_part_no);
    if (existing && mergeWithExisting) {
      // Merged total also gets snapped so the union is a valid multiple.
      const mergedRaw = existing.quantity + snappedQty;
      const merged = snapQty(mergedRaw);
      setCart(cart.map(item =>
        item.jp_part_no === product.jp_part_no
          ? { ...item, quantity: merged }
          : item
      ));
      toast.success(`Updated ${product.part_name}: ${existing.quantity} + ${snappedQty} = ${merged} PC`);
    } else if (!existing) {
      setCart([...cart, { ...product, quantity: snappedQty }]);
      toast.success(`Added ${snappedQty} × ${product.part_name} to cart`);
    }

    // Reset state
    setAddToCartProduct(null);
    setAddToCartQuantity(0);
    setAddToCartHandshakePrice('');
    setDuplicateItemConfirm(null);
  };
  
  // Handle duplicate item confirmation
  const handleDuplicateConfirm = (merge) => {
    if (!duplicateItemConfirm) return;
    
    if (merge) {
      // Merge quantities
      addItemToCart(
        duplicateItemConfirm.product, 
        duplicateItemConfirm.newQty, 
        duplicateItemConfirm.handshakePrice, 
        true
      );
    } else {
      // Cancel - just close dialogs
      setDuplicateItemConfirm(null);
      setAddToCartProduct(null);
      setAddToCartQuantity(0);
      setAddToCartHandshakePrice('');
    }
  };
  
  // Helper to select all text on focus
  const handleSelectOnFocus = (e) => {
    e.target.select();
  };

  const addToCart = (product) => {
    // 2026-02-20 RC-151 — Default qty on quick-add is the warehouse
    // MOQ itself, not 1, so admin doesn't have to correct it after.
    const moq = Math.max(1, parseInt(product.warehouse_moq || 1, 10));
    const existing = cart.find(item => item.jp_part_no === product.jp_part_no);
    if (existing) {
      // Show confirmation for duplicate
      setDuplicateItemConfirm({
        product: product,
        existingQty: existing.quantity,
        newQty: moq,
        handshakePrice: ''
      });
    } else {
      setCart([...cart, { ...product, quantity: moq }]);
      toast.success(`Added ${product.part_name}${moq > 1 ? ` (${moq} PC)` : ''} to cart`);
    }
  };

  const updateQuantity = (partNo, delta) => {
    setCart(cart.map(item => {
      if (item.jp_part_no === partNo) {
        // 2026-02-20 RC-151 — step by warehouse_moq when set (>=2).
        // If current qty isn't already a valid multiple (legacy add
        // before the snap-on-add fix, or a hydrated existing order
        // line), snap UP first so the first click lands on a clean
        // multiple, THEN step by moq on subsequent clicks.
        const step = Math.max(1, parseInt(item.warehouse_moq || 1, 10));
        const current = parseInt(item.quantity || 0, 10);
        const isClean = step <= 1 || current % step === 0;
        let newQty;
        if (isClean) {
          newQty = Math.max(step, current + (delta > 0 ? step : -step));
        } else {
          // First bring qty onto a multiple boundary.
          newQty = delta > 0
            ? Math.ceil(current / step) * step
            : Math.max(step, Math.floor(current / step) * step);
          if (newQty === current) {
            // Already on boundary after floor/ceil (edge case) — step normally.
            newQty = Math.max(step, current + (delta > 0 ? step : -step));
          }
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  // Direct quantity input handler
  const setQuantityDirect = (partNo, value) => {
    const qty = parseInt(value) || 1;
    setCart(cart.map(item => {
      if (item.jp_part_no === partNo) {
        // 2026-02-20 RC-151 — round UP to warehouse_moq multiple.
        const moq = Math.max(1, parseInt(item.warehouse_moq || 1, 10));
        const snapped = moq > 1
          ? Math.ceil(Math.max(1, qty) / moq) * moq
          : Math.max(1, qty);
        return { ...item, quantity: snapped };
      }
      return item;
    }));
  };

  const removeFromCart = (partNo) => {
    setCart(cart.filter(item => item.jp_part_no !== partNo));
    // Also remove any handshake price
    setInlineHandshakePrices(prev => {
      const updated = { ...prev };
      delete updated[partNo];
      return updated;
    });
  };

  // Calculate totals - use handshake price > existing special price > discount
  const calculateItemPrice = (item) => {
    const mrp = item.mrp || 0;
    let discountedPrice;
    
    // Priority: handshake > existing special price > customer discount
    if (inlineHandshakePrices[item.jp_part_no]) {
      discountedPrice = inlineHandshakePrices[item.jp_part_no];
    } else if (specialPrices[item.jp_part_no]) {
      discountedPrice = specialPrices[item.jp_part_no];
    } else {
      discountedPrice = mrp * (1 - customerDiscount / 100);
    }
    return discountedPrice * item.quantity;
  };
  
  // Get the effective price for an item (for display)
  const getItemPrice = (item) => {
    const mrp = item.mrp || 0;
    // Priority: handshake > existing special price > customer discount
    if (inlineHandshakePrices[item.jp_part_no]) {
      return inlineHandshakePrices[item.jp_part_no];
    }
    if (specialPrices[item.jp_part_no]) {
      return specialPrices[item.jp_part_no];
    }
    return mrp * (1 - customerDiscount / 100);
  };
  
  // Check if item has special price (either existing or handshake)
  const hasSpecialPrice = (item) => {
    return !!specialPrices[item.jp_part_no] || !!inlineHandshakePrices[item.jp_part_no];
  };
  
  // Check if item has handshake price entered in popup
  const hasInlineHandshakePrice = (item) => {
    return !!inlineHandshakePrices[item.jp_part_no];
  };

  const cartTotal = cart.reduce((sum, item) => sum + calculateItemPrice(item), 0);
  
  // Calculate total for saving draft
  const calculateTotal = () => cartTotal;

  // Submit Order
  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    // Validate dispatch details - Transporter required for 'transport' and 'courier', NOT for 'local'
    if (dispatchMode !== 'local' && !transporterName.trim()) {
      toast.error('Transporter name is required for Transport/Courier dispatch');
      return;
    }

    setSubmitting(true);
    try {
      // First, save any inline handshake prices as permanent special prices
      if (Object.keys(inlineHandshakePrices).length > 0) {
        try {
          const specialPriceItems = Object.entries(inlineHandshakePrices).map(([partNo, price]) => {
            const cartItem = cart.find(item => item.jp_part_no === partNo);
            return {
              jp_part_no: partNo,
              part_name: cartItem?.part_name || partNo,
              original_price: cartItem?.mrp || 0,
              special_price: price
            };
          });
          
          await axios.post(`${API}/price-requests/admin/create-special-prices`, {
            customer_id: selectedCustomer.customer_id,
            items: specialPriceItems
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success(`${specialPriceItems.length} handshake price(s) saved permanently`);
        } catch (err) {
          console.error('Error saving handshake prices:', err);
          // Continue with order even if special price save fails
        }
      }
      
      const orderData = {
        customer_mobile: selectedCustomer.mobile,
        items: cart.map(item => {
          // Priority: inline handshake > existing special price > discount
          let effectivePrice;
          let hasSpecial = false;
          
          if (inlineHandshakePrices[item.jp_part_no]) {
            effectivePrice = inlineHandshakePrices[item.jp_part_no];
            hasSpecial = true;
          } else if (specialPrices[item.jp_part_no]) {
            effectivePrice = specialPrices[item.jp_part_no];
            hasSpecial = true;
          } else {
            effectivePrice = item.mrp * (1 - customerDiscount / 100);
          }
          
          return {
            jp_part_no: item.jp_part_no,
            part_name: item.part_name,
            quantity: item.quantity,
            price: item.mrp,
            discounted_price: effectivePrice,
            has_special_price: hasSpecial
          };
        }),
        discount_percentage: customerDiscount,
        dispatch_mode: dispatchMode,
        preferred_transporter: dispatchMode === 'local' ? '' : transporterName,
        dispatch_remarks: dispatchRemarks,
        notes: `Order created by admin on behalf of customer`
      };

      await axios.post(`${API}/admin/orders/create-for-customer`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Delete any saved draft for this customer
      if (selectedCustomer?.customer_id) {
        await deleteDraftForCustomer(selectedCustomer.customer_id);
      }

      toast.success('Order created successfully!');
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating order:', error);
      // 2026-02-20 RC-151 — Warehouse-MOQ violation detail.
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'WAREHOUSE_MOQ_VIOLATION') {
        const lines = (detail.violations || [])
          .map(v => `${v.jp_part_no}: entered ${v.qty} → must be multiple of ${v.moq} (try ${v.suggested_qty})`)
          .join('\n');
        toast.error(`Warehouse MOQ violation:\n${lines}`, { duration: 10000 });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to create order');
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setCustomerSearch('');
    setCustomers([]);
    setSelectedCustomer(null);
    setCustomerDiscount(0);
    setSpecialPrices({});
    setInlineHandshakePrices({});
    setProductSearch('');
    setSearchResults([]);
    setCart([]);
    setDispatchMode('transport');
    setTransporterName('');
    setDispatchRemarks('');
    // Clear the saved draft
    localStorage.removeItem(ORDER_DRAFT_KEY);
  };

  // Handle X button click - show confirmation if unsaved changes
  const handleCloseButtonClick = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      resetForm();
      onClose();
    }
  };
  
  // Confirm close - discard changes
  const confirmClose = () => {
    setShowCloseConfirm(false);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    // This is called by Dialog onOpenChange - we prevent it
    // Close only via X button which calls handleCloseButtonClick
  };

  return (
    <>
    {/* Main Order Dialog */}
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white"
        preventClose={true}
        onCloseClick={handleCloseButtonClick}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-red-600" />
            Create Customer Order
            {hasUnsavedChanges && (
              <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">
                <AlertCircle className="w-3 h-3 mr-1" />
                Unsaved
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= s ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              <span className={`ml-2 text-sm ${step >= s ? 'text-gray-900' : 'text-gray-500'}`}>
                {s === 1 ? 'Select Customer' : s === 2 ? 'Build Cart' : 'Review'}
              </span>
              {s < 3 && <div className={`w-12 h-0.5 mx-2 ${step > s ? 'bg-red-600' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Customer */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Pending for Completion - Server Drafts */}
            {serverDrafts.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                    <Clock className="w-4 h-4" />
                    Pending for Completion ({serverDrafts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 space-y-2 max-h-48 overflow-y-auto">
                  {serverDrafts.map((draft) => (
                    <div
                      key={draft.id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {draft.company_name || draft.customer_name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-3">
                          <span>{draft.item_count} items</span>
                          <span className="text-green-600 font-medium">₹{(draft.total_amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          Saved: {new Date(draft.updated_at).toLocaleDateString()} {new Date(draft.updated_at).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white h-8"
                          onClick={() => resumeDraft(draft)}
                        >
                          <Play className="w-3 h-3 mr-1" /> Resume
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50 h-8 w-8 p-0"
                          onClick={() => deleteServerDraft(draft.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            
            {loadingDrafts && (
              <div className="text-center py-2 text-gray-500 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                Loading saved drafts...
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by customer name or company name..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onFocus={handleSelectOnFocus}
                className="pl-10 h-12 text-lg"
                autoFocus
              />
            </div>

            {loadingCustomers && (
              <div className="text-center py-4 text-gray-500">Searching...</div>
            )}

            {customers.length > 0 && (
              <div className="border rounded-lg max-h-80 overflow-y-auto">
                {customers.map((customer) => (
                  <div
                    key={customer.mobile}
                    onClick={() => handleSelectCustomer(customer)}
                    className={`p-4 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isBangladeshCustomer(customer) ? 'bg-green-50 hover:bg-green-100 border-l-4 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {isBangladeshCustomer(customer) && <span className="text-lg">🇧🇩</span>}
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{customer.name}</span>
                        </div>
                        {customer.company_name && (
                          <div className="flex items-center gap-2 mt-1">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{customer.company_name}</span>
                          </div>
                        )}
                        <div className="text-sm text-gray-500 mt-1">
                          {customer.country_code ? `+${customer.country_code} ` : ''}{customer.mobile}
                        </div>
                      </div>
                      <div className="text-right">
                        {isBangladeshCustomer(customer) ? (
                          <Badge className="bg-green-600 text-white text-xs">Bangladesh Export</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {customer.category || 'RETAILER'}
                          </Badge>
                        )}
                        {customer.discount_percentage > 0 && (
                          <div className="text-sm text-green-600 mt-1">
                            {customer.discount_percentage}% discount
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {customerSearch.length >= 2 && customers.length === 0 && !loadingCustomers && (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No customers found matching "{customerSearch}"</p>
                <Button 
                  variant="outline" 
                  onClick={() => setShowQuickRegister(true)}
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Register New Customer
                </Button>
              </div>
            )}
            
            {/* Quick Register Button - always visible */}
            <div className="mt-4 pt-4 border-t">
              <Button 
                variant="ghost" 
                onClick={() => setShowQuickRegister(true)}
                className="w-full text-blue-600 hover:bg-blue-50"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Register New Customer
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Build Cart */}
        {step === 2 && selectedCustomer && (
          <div className="space-y-4">
            {/* Selected Customer Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{selectedCustomer.name}</div>
                    {selectedCustomer.company_name && (
                      <div className="text-sm text-gray-600">{selectedCustomer.company_name}</div>
                    )}
                    <div className="text-sm text-gray-500">{selectedCustomer.mobile}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    {editingDiscount ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={newDiscount}
                          onChange={(e) => setNewDiscount(e.target.value)}
                          className="w-20 h-8"
                          min="0"
                          max="100"
                        />
                        <span className="text-sm">%</span>
                        <Button size="sm" onClick={handleUpdateDiscount} className="bg-green-600 hover:bg-green-700">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingDiscount(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-green-600">{customerDiscount}% discount</span>
                        <Button size="sm" variant="outline" onClick={() => setEditingDiscount(true)}>
                          Edit
                        </Button>
                      </div>
                    )}
                    <Button variant="outline" size="sm" onClick={() => { setSelectedCustomer(null); setStep(1); }}>
                      Change
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search products by part number or name..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={handleSelectOnFocus}
                className="pl-10 h-12"
              />
            </div>

            {/* Search Results - Custom Product Cards with Images */}
            {loadingProducts && (
              <div className="text-center py-4 text-gray-500">Searching products...</div>
            )}

            {searchResults.length > 0 && (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 font-medium">
                  Found {searchResults.length} products - Click "Add" to add with quantity
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto p-1">
                  {searchResults.map((product) => {
                    const unitPrice = product.mrp || 0;
                    // Check for special price first, then use general discount
                    const hasSpecial = !!specialPrices[product.jp_part_no];
                    const discountedPrice = hasSpecial 
                      ? specialPrices[product.jp_part_no]
                      : unitPrice * (1 - customerDiscount / 100);
                    const imageUrl = productImages[product.jp_part_no];
                    
                    return (
                      <div key={product.jp_part_no} className="border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                        {/* Product Image */}
                        <div className="h-28 bg-gray-50 flex items-center justify-center p-2">
                          {imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={product.part_name}
                              className="max-h-full max-w-full object-contain"
                            />
                          ) : product.image ? (
                            <div className="text-xs text-gray-400">Loading...</div>
                          ) : (
                            <div className="text-xs text-gray-400">No Image</div>
                          )}
                        </div>
                        
                        {/* Product Info */}
                        <div className="p-3">
                          <h4 className="font-medium text-gray-900 text-sm line-clamp-2 min-h-[40px]">{product.part_name}</h4>
                          <p className="text-xs text-gray-500 mt-1">Part No: <span className="font-mono text-red-600 font-bold">{formatJpWithAlias(product)}</span></p>
                          {product.oe_part_no && (
                            <p className="text-xs text-gray-400 truncate">{formatOeDisplay(product.oe_part_no, true)}</p>
                          )}
                          
                          <div className="flex items-center justify-between mt-3 pt-2 border-t">
                            <div>
                              {(customerDiscount > 0 || hasSpecial) ? (
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-400 line-through">MRP: ₹{unitPrice.toFixed(2)}</span>
                                  <span className={`text-base font-bold ${hasSpecial ? 'text-amber-600' : 'text-green-600'}`}>
                                    {hasSpecial ? 'SPECIAL: ' : ''}₹{discountedPrice.toFixed(2)}
                                  </span>
                                  {hasSpecial && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-1 rounded mt-1 inline-block w-fit">
                                      Negotiated Rate
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-base font-bold text-gray-900">₹{unitPrice.toFixed(2)}</span>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => openAddToCartDialog(product)} 
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              <Plus className="w-4 h-4 mr-1" /> Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {productSearch.length >= 2 && searchResults.length === 0 && !loadingProducts && (
              <div className="text-center py-8 text-gray-500">
                No products found matching "{productSearch}"
              </div>
            )}

            {/* Cart */}
            {cart.length > 0 && (
              <Card className="border-2 border-green-200">
                <CardHeader className="py-3 bg-green-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-green-600" />
                    Cart ({cart.length} items)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-60 overflow-y-auto">
                    {cart.map((item) => (
                      <div key={item.jp_part_no} className="p-3 border-b flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{item.part_name}</div>
                          <div className="text-sm text-gray-500">{formatJpWithAlias(item)}</div>
                          {hasSpecialPrice(item) && (
                            <Badge className={`text-xs mt-1 ${hasInlineHandshakePrice(item) ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                              {hasInlineHandshakePrice(item) ? 'Handshake Price' : 'Special Price'}
                            </Badge>
                          )}
                          {/* 2026-02-20 RC-151 · warehouse-MOQ pack-of hint. */}
                          {parseInt(item.warehouse_moq || 1, 10) > 1 && (
                            <div
                              className="text-[10px] text-amber-700 font-medium mt-0.5"
                              data-testid={`wh-moq-hint-${item.jp_part_no}`}
                            >
                              MOQ {item.warehouse_moq} · pack of {item.warehouse_moq}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => updateQuantity(item.jp_part_no, -1)} className="h-8 w-8 p-0">
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => setQuantityDirect(item.jp_part_no, e.target.value)}
                              onFocus={handleSelectOnFocus}
                              className="w-16 h-8 text-center font-medium"
                            />
                            <Button size="sm" variant="outline" onClick={() => updateQuantity(item.jp_part_no, 1)} className="h-8 w-8 p-0">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Price display (readonly) */}
                          <div className="w-24 text-right">
                            <div className={`font-medium ${hasInlineHandshakePrice(item) ? 'text-blue-600' : hasSpecialPrice(item) ? 'text-amber-600' : 'text-green-600'}`}>
                              ₹{getItemPrice(item).toFixed(2)}
                            </div>
                            {hasSpecialPrice(item) && (
                              <div className="text-xs text-gray-400 line-through">₹{item.mrp?.toFixed(2)}</div>
                            )}
                          </div>
                          
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeFromCart(item.jp_part_no)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 bg-gray-50 flex items-center justify-between border-t">
                    <span className="font-bold text-lg">Total:</span>
                    <span className="font-bold text-xl text-green-600">₹{cartTotal.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={saveProgressToServer}
                  disabled={cart.length === 0 || savingDraft}
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingDraft ? 'Saving...' : 'Save Progress'}
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  disabled={cart.length === 0}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Review Order
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review & Submit */}
        {step === 3 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="py-3 bg-gray-50">
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Customer</Label>
                    <div className="font-medium">{selectedCustomer?.name}</div>
                    {selectedCustomer?.company_name && (
                      <div className="text-sm text-gray-600">{selectedCustomer.company_name}</div>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone</Label>
                    <div className="font-medium">{selectedCustomer?.mobile}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Discount Applied</Label>
                    <div className="font-medium text-green-600">{customerDiscount}%</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Total Items</Label>
                    <div className="font-medium">{cart.reduce((sum, item) => sum + item.quantity, 0)}</div>
                  </div>
                  {Object.keys(specialPrices).length > 0 && (
                    <div>
                      <Label className="text-gray-500">Special Prices</Label>
                      <div className="font-medium text-amber-600">{Object.keys(specialPrices).length} items</div>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <Label className="text-gray-500 mb-2 block">Items</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {cart.map((item) => (
                      <div key={item.jp_part_no} className="flex justify-between text-sm">
                        <span>
                          {item.part_name} x {item.quantity}
                          {hasSpecialPrice(item) && <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Special</Badge>}
                        </span>
                        <span className={`font-medium ${hasSpecialPrice(item) ? 'text-amber-600' : ''}`}>
                          ₹{calculateItemPrice(item).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4 flex justify-between items-center">
                  <span className="text-xl font-bold">Grand Total</span>
                  <span className="text-2xl font-bold text-green-600">₹{cartTotal.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Dispatch Details Card */}
            <Card>
              <CardHeader className="py-3 bg-blue-50">
                <CardTitle className="text-lg">Dispatch Details</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                
                {/* Dispatch Mode Selection - Available for ALL customers */}
                <div className="space-y-4">
                  {isDelhiCustomer && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        Delhi Customer — Dispatch locked to Local
                        {isMasterAdmin && <span className="text-xs text-amber-600 ml-2">(Master Admin can override)</span>}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dispatch_mode" className="font-semibold">
                        Dispatch Method <span className="text-red-600">*</span>
                      </Label>
                      <select
                        id="dispatch_mode"
                        value={dispatchMode}
                        onChange={(e) => setDispatchMode(e.target.value)}
                        disabled={isDispatchLocked}
                        className={`w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${isDispatchLocked ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
                        data-testid="dispatch-mode-select"
                      >
                        <option value="transport">Transport</option>
                        <option value="courier">Courier</option>
                        <option value="local">Local Dispatch (Loose)</option>
                      </select>
                    </div>
                    {/* Transporter field - only show for transport/courier, NOT for local */}
                    {dispatchMode !== 'local' ? (
                      <div>
                        <Label htmlFor="transporter_name" className="font-semibold">
                          {dispatchMode === 'courier' ? 'Courier' : 'Transporter'} Name <span className="text-red-600">*</span>
                        </Label>
                        <TransporterComboBox
                          value={transporterName}
                          onChange={setTransporterName}
                          customerId={selectedCustomer?.customer_id}
                          dispatchMode={dispatchMode}
                          placeholder={dispatchMode === 'courier' ? 'Enter courier name' : 'Enter transporter name'}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <p className="text-sm text-green-700 bg-green-50 p-3 rounded border border-green-200 mt-6">
                          Local dispatch - No transporter, carton packing, or LR required. Order closes after e-invoice upload.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Remarks field - always available */}
                  <div>
                    <Label htmlFor="dispatch_remarks" className="font-semibold">
                      Remarks <span className="text-gray-400">(Optional)</span>
                    </Label>
                    <Input
                      id="dispatch_remarks"
                      type="text"
                      value={dispatchRemarks}
                      onChange={(e) => setDispatchRemarks(e.target.value)}
                      className="mt-1"
                      placeholder="E.g., Will collect from shop, Send to branch..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back to Cart
              </Button>
              <Button 
                onClick={handleSubmitOrder} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 px-8"
              >
                {submitting ? 'Creating Order...' : 'Create Order'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Add to Cart Dialog with Quantity Selection */}
    <Dialog open={!!addToCartProduct} onOpenChange={() => setAddToCartProduct(null)}>
      <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => {
            e.preventDefault();
            // Focus and select the quantity input when dialog opens
            setTimeout(() => {
              if (qtyInputRef.current) {
                qtyInputRef.current.focus();
                qtyInputRef.current.select();
              }
            }, 0);
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">Add to Cart</DialogTitle>
          </DialogHeader>
          
          {addToCartProduct && (() => {
            const existingSpecialPrice = specialPrices[addToCartProduct.jp_part_no];
            const defaultPrice = existingSpecialPrice || (addToCartProduct.mrp || 0) * (1 - customerDiscount / 100);
            // Calculate effective price based on handshake input
            const handshakeValue = addToCartHandshakePrice.trim();
            const effectivePrice = (handshakeValue && parseFloat(handshakeValue) > 0) 
              ? parseFloat(handshakeValue) 
              : defaultPrice;
            const hasHandshake = handshakeValue && parseFloat(handshakeValue) > 0;
            
            return (
            <div className="space-y-4">
              {/* Product Preview */}
              <div className="flex gap-4">
                <div className="w-20 h-20 bg-gray-50 rounded flex-shrink-0 flex items-center justify-center">
                  {productImages[addToCartProduct.jp_part_no] ? (
                    <img 
                      src={productImages[addToCartProduct.jp_part_no]} 
                      alt={addToCartProduct.part_name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No Image</span>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 text-sm">{addToCartProduct.part_name}</h4>
                  <p className="text-xs text-gray-500">Part No: <span className="font-mono text-red-600">{formatJpWithAlias(addToCartProduct)}</span></p>
                  <div className="mt-1">
                    <span className="text-xs text-gray-400">MRP: ₹{(addToCartProduct.mrp || 0).toFixed(2)}</span>
                    {customerDiscount > 0 && (
                      <span className="text-xs text-green-600 ml-2">({customerDiscount}% discount)</span>
                    )}
                  </div>
                  {existingSpecialPrice && !hasHandshake && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mt-1 inline-block">
                      Existing Special: ₹{existingSpecialPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>

              {/* Quantity and Handshake Price Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Quantity Selection */}
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium">
                    Quantity
                    {parseInt(addToCartProduct?.warehouse_moq || 1, 10) > 1 && (
                      <span
                        className="ml-2 text-xs font-semibold text-amber-700"
                        data-testid="add-dialog-wh-moq-hint"
                      >
                        · MOQ {addToCartProduct.warehouse_moq} · pack of {addToCartProduct.warehouse_moq}
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2">
                    {/* 2026-02-20 RC-151 — step by warehouse_moq. */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const moq = Math.max(1, parseInt(addToCartProduct?.warehouse_moq || 1, 10));
                        setAddToCartQuantity(Math.max(moq, addToCartQuantity - moq));
                      }}
                      className="h-10 w-10 p-0"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      ref={qtyInputRef}
                      type="number"
                      min="0"
                      value={addToCartQuantity}
                      onChange={(e) => {
                        const raw = Math.max(0, parseInt(e.target.value) || 0);
                        const moq = Math.max(1, parseInt(addToCartProduct?.warehouse_moq || 1, 10));
                        if (moq <= 1 || raw === 0) {
                          setAddToCartQuantity(raw);
                        } else {
                          setAddToCartQuantity(Math.ceil(raw / moq) * moq);
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      autoFocus
                      className="w-20 h-10 text-center text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const moq = Math.max(1, parseInt(addToCartProduct?.warehouse_moq || 1, 10));
                        const current = parseInt(addToCartQuantity || 0, 10);
                        const isClean = moq <= 1 || current % moq === 0;
                        if (isClean) {
                          setAddToCartQuantity(Math.max(moq, current + moq));
                        } else {
                          setAddToCartQuantity(Math.ceil(current / moq) * moq);
                        }
                      }}
                      className="h-10 w-10 p-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Handshake Price Input */}
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium">
                    Handshake Price <span className="text-xs text-gray-400">(optional)</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={addToCartHandshakePrice}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Only allow numbers and decimal
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setAddToCartHandshakePrice(val);
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      placeholder={defaultPrice.toFixed(2)}
                      className="h-10 pl-7 text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  {hasHandshake && (
                    <p className="text-xs text-blue-600">Will be saved as special price</p>
                  )}
                </div>
              </div>

              {/* Total */}
              <div className={`rounded-lg p-3 flex justify-between items-center ${hasHandshake ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                <span className="font-medium text-gray-700">
                  Unit Price: <span className={`font-bold ${hasHandshake ? 'text-blue-600' : existingSpecialPrice ? 'text-amber-600' : 'text-green-600'}`}>₹{effectivePrice.toFixed(2)}</span>
                </span>
                <span className={`text-xl font-bold ${hasHandshake ? 'text-blue-600' : existingSpecialPrice ? 'text-amber-600' : 'text-green-600'}`}>
                  Total: ₹{(effectivePrice * addToCartQuantity).toFixed(2)}
                </span>
              </div>
            </div>
          );
          })()}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddToCartProduct(null)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmAddToCart}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* Close Confirmation Dialog */}
    <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
      <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Unsaved Changes
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-gray-600">
            You have unsaved items in your cart. Are you sure you want to close?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Your progress will be lost.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowCloseConfirm(false)}>
            Continue Editing
          </Button>
          <Button 
            onClick={confirmClose}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Discard & Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* Draft Recovery Dialog */}
    <Dialog open={showDraftRecovery} onOpenChange={setShowDraftRecovery}>
      <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
            Restore Previous Order?
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-gray-600">
            You have an unsaved order from your previous session.
          </p>
          {savedDraft && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="text-gray-700">
                <strong>Customer:</strong> {savedDraft.selectedCustomer?.company_name || savedDraft.selectedCustomer?.name || 'Not selected'}
              </p>
              <p className="text-gray-700">
                <strong>Items in cart:</strong> {savedDraft.cart?.length || 0}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Saved: {savedDraft.savedAt ? new Date(savedDraft.savedAt).toLocaleString() : 'Unknown'}
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={discardDraft}>
            Start Fresh
          </Button>
          <Button 
            onClick={restoreDraft}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Restore Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* Quick Register Customer Modal */}
    <QuickRegisterCustomer
      isOpen={showQuickRegister}
      onClose={() => setShowQuickRegister(false)}
      onCustomerRegistered={(customer) => {
        // Check if Bangladesh customer
        if (customer.country_code === '880') {
          setBangladeshCustomer(customer);
          setShowBangladeshModal(true);
        } else {
          handleSelectCustomer(customer);
        }
      }}
      token={token}
    />
    
    {/* Bangladesh Export Order Modal - shown when Bangladesh customer is selected */}
    <BangladeshExportOrderModal
      isOpen={showBangladeshModal}
      onClose={() => {
        setShowBangladeshModal(false);
        setBangladeshCustomer(null);
      }}
      onCreated={(data) => {
        setShowBangladeshModal(false);
        setBangladeshCustomer(null);
        onClose(); // Close the main modal too
        toast.success(`Bangladesh export order created: ${data.order_id}`);
      }}
      token={token}
      preSelectedCustomer={bangladeshCustomer}
    />
    
    {/* Duplicate Item Confirmation Dialog */}
    <Dialog open={!!duplicateItemConfirm} onOpenChange={() => setDuplicateItemConfirm(null)}>
      <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Item Already in Cart
          </DialogTitle>
        </DialogHeader>
        {duplicateItemConfirm && (
          <div className="py-4">
            <p className="text-gray-700 font-medium">
              {duplicateItemConfirm.product?.part_name}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Part No: <span className="font-mono text-red-600">{formatJpWithAlias(duplicateItemConfirm.product || {})}</span>
            </p>
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-800">
                This item is already in your cart with <strong>{duplicateItemConfirm.existingQty} PC</strong>.
              </p>
              <p className="text-amber-700 mt-2">
                Do you want to add <strong>{duplicateItemConfirm.newQty} PC</strong> to the existing quantity?
              </p>
              <p className="text-amber-600 mt-2 text-sm">
                New total: <strong>{duplicateItemConfirm.existingQty + duplicateItemConfirm.newQty} PC</strong>
              </p>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleDuplicateConfirm(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleDuplicateConfirm(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Yes, Add to Existing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CreateCustomerOrder;
